/**
 * Booking app: ONE app mounted into every block present on the page (CHANGE 2026-10-18 §1.2, 01 §14).
 *
 * Rules (CLAUDE.md invariants 1, 10, 12; 02 §9; 01 §13):
 * - One state (booking.js), one API client, one session for all blocks. Blocks never call the API.
 * - A missing block breaks nothing; a duplicate block warns in the console and renders nothing.
 * - Call budget (§1.4): a page with the full search, results or basket loads its data once (POST /start, then
 *   POST /session with the calendar); a page with only compact searches calls nothing until the picker opens.
 * - No business logic here: valid dates, availability, prices, totals, allocation and explanations come from
 *   the API. Every visible sentence is a label (page HTML) or a catalogue template; values via Intl.
 */
import { clear } from './dom.js';
import { createBooking } from './booking.js';
import { createMessages } from './messages.js';
import { mountSearch, mountResults, mountBasket } from './blocks.js';
import { mountCompact } from './compact.js';

/**
 * @param {Element[]} nodes Block mount points (.pap-block[data-pap-block]).
 * @param {{api: Object, i18n: Object, today: string, contact?: Object|null, display?: Object, params?: URLSearchParams, navigate?: Function}} options
 * @returns {{booking: Object, state: Object, blocks: string[]}}
 */
export function mountBlocks(nodes, options) {
	const { api, i18n } = options;
	const booking = createBooking({ api, today: options.today, month: options.params?.get('mois') ?? null });
	// Amounts in the property's currency, from the server (Phase 3): set as soon as /start answers.
	booking.subscribe((s) => {
		if (s.config?.property?.currency) {
			i18n.useCurrency(s.config.property.currency);
		}
	});
	const kinds = new Set();
	const ctx = {
		booking,
		i18n,
		msg: createMessages(i18n, options.contact || null),
		display: options.display || {},
		has: (kind) => kinds.has(kind),
		params: options.params || new URLSearchParams(),
		navigate: options.navigate || ((url) => window.location.assign(url))
	};

	const mounts = [];
	let needsData = false;
	for (const node of nodes) {
		const kind = node.dataset.papBlock;
		if (!['search', 'results', 'basket'].includes(kind)) {
			continue;
		}
		const id = kind === 'search' && node.dataset.mode === 'compact' ? 'compact' : kind;
		if (kinds.has(id)) {
			console.warn(`[papounan] block "${id}" appears twice on this page: only the first one is used.`);
			clear(node);
			node.hidden = true;
			continue;
		}
		kinds.add(id);
		mounts.push([id, node]);
		needsData = needsData || id !== 'compact';
	}

	for (const [kind, node] of mounts) {
		// mois (0.9.6): the shortcode's first calendar month (validated by PHP and by the calendar).
		const place = { target: node.dataset.target || '', room: Number(node.dataset.room) || null, month: node.dataset.mois || null };
		if (kind === 'compact') {
			// Shares the page's store; its panel (guests + calendar) is a lazy chunk.
			mountCompact(node, { ...ctx, getBooking: async () => booking, prefetchStart: needsData ? null : () => api.prefetchStart?.(), loadPanel: () => import('./compact-panel.js') }, place);
		} else if (kind === 'search') {
			mountSearch(node, ctx, place);
		} else if (kind === 'results') {
			mountResults(node, ctx);
		} else {
			mountBasket(node, ctx);
		}
	}

	if (needsData) {
		// Preselection from the URL (compact search, "Recherche séjour" component); search runs by itself when
		// complete and a results block is here.
		booking.applyParams(options.params || new URLSearchParams(), { autoSearch: kinds.has('results') });
	}
	return { booking, state: booking.state, blocks: [...kinds] };
}
